import { ActionsHeaderCell } from '@/components/ActionsHeaderCell';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Button,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { TabularText } from '@/components/TabularText';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { importsApi, type ImportBatch } from '@/api/imports';
import { errorCopy } from '@/api/errorCopy';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { ErrorState } from '@/components/ErrorState';
import { StatusChip } from '@/components/StatusChip';
import { useConfirm } from '@/components/useConfirm';
import { formatInstant } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { useMe } from '@/session/meHooks';
import { beforeOpeningLabels } from './beforeOpeningLabels';

interface ImportsTabProps {
  accountId: number;
  canStart: boolean;
}

export function ImportsTab({ accountId, canStart }: ImportsTabProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const me = useMe();
  const navigate = useNavigate();
  const timeZone = me.user.timezone ?? 'UTC';
  const [rows, setRows] = useState<ImportBatch[] | null>(null);
  const [loadFailure, setLoadFailure] = useState<{ error: unknown } | null>(null);
  const discarding = useConfirm<ImportBatch>();
  const started = useRef(false);

  const load = useCallback(async () => {
    try {
      setRows(await importsApi.list(accountId));
      setLoadFailure(null);
    } catch (error) {
      setLoadFailure({ error });
    }
  }, [accountId]);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void load();
  }, [load]);

  if (loadFailure !== null) {
    return (
      <ErrorState
        error={loadFailure.error}
        what={t('errors.what.imports')}
        onRetry={() => void load()}
      />
    );
  }

  if (rows === null) return <Typography>{t('common.loading')}</Typography>;

  return (
    <Stack spacing={2}>
      {canStart && (
        <Button
          variant="contained"
          component={RouterLink}
          to={`/me/accounts/${accountId}/import?tab=imports`}
          sx={{ alignSelf: 'flex-start' }}
        >
          {t('imports.new')}
        </Button>
      )}
      <Table size="small" aria-label={t('accounts.tabImports')}>
        <TableHead>
          <TableRow>
            <TableCell>{t('imports.colFilename')}</TableCell>
            <TableCell>{t('imports.colUploaded')}</TableCell>
            <TableCell>{t('imports.colStatus')}</TableCell>
            <TableCell>{t('imports.colImported')}</TableCell>
            <TableCell>{t('imports.colDuplicate')}</TableCell>
            <TableCell>{t('imports.colRejected')}</TableCell>
            <TableCell>{t('imports.colBeforeOpening')}</TableCell>
            <ActionsHeaderCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => {
            const uncommitted = row.status === 'UPLOADED' || row.status === 'PREVIEWED';
            const beforeOpening = beforeOpeningLabels(t, row);
            return (
              <TableRow
                key={row.id}
                hover
                sx={{ cursor: 'pointer' }}
                onClick={() => navigate(`/me/accounts/${accountId}/import/${row.id}?tab=imports`)}
              >
                <TableCell>{row.originalFilename}</TableCell>
                <TableCell>
                  <TabularText>{formatInstant(row.uploadedAt, locale, timeZone)}</TabularText>
                  {row.filePurgedAt !== null && (
                    <Typography variant="caption" component="div" sx={{ color: 'text.secondary' }}>
                      {t('imports.filePurged')}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <StatusChip domain="importBatch" status={row.status} />
                  {row.errorSummary !== null && (
                    <Typography variant="caption" component="div">
                      {row.errorSummary}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>{row.rowsImported}</TableCell>
                <TableCell>{row.rowsDuplicate}</TableCell>
                <TableCell>{row.rowsRejected}</TableCell>
                <TableCell>
                  {beforeOpening.length > 0
                    ? beforeOpening.map((line) => (
                        <Typography key={line} variant="body2">
                          {line}
                        </Typography>
                      ))
                    : '—'}
                </TableCell>
                <TableCell onClick={(event) => event.stopPropagation()}>
                  {uncommitted && (
                    <Stack direction="row" sx={{ gap: 1 }}>
                      <Button
                        size="small"
                        component={RouterLink}
                        to={`/me/accounts/${accountId}/import/${row.id}?tab=imports&step=confirm`}
                      >
                        {t('imports.resume')}
                      </Button>
                      <Button size="small" color="error" onClick={() => discarding.ask(row)}>
                        {t('imports.discard')}
                      </Button>
                    </Stack>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      {/* Outside the table: a click inside the dialog must not bubble to a row's navigation. */}
      {discarding.target !== null && (
        <ConfirmDialog
          title={t('imports.discardTitle', { file: discarding.target.originalFilename })}
          consequence={t('imports.discardConsequence')}
          confirmLabel={t('imports.discardConfirm')}
          destructive
          busy={discarding.busy}
          error={discarding.error}
          onConfirm={() =>
            void discarding.run(
              async (row) => {
                await importsApi.discard(accountId, row.id);
                await load();
              },
              (error) => errorCopy(t, error, 'accounts.errorGeneric'),
            )
          }
          onCancel={discarding.cancel}
        />
      )}
    </Stack>
  );
}
